import { type Filter, type LocationArea } from '@kijiji/generated/graphql-types'
import { ChevronLeftSmallIcon } from '@kijiji/icons/src/icons'
import { theme } from '@kijiji/theme'
import dynamic from 'next/dynamic'
import { useTranslation } from 'next-i18next'

import { TRANSLATION_KEYS } from '@/constants/localization'
import { FILTER_CANONICAL } from '@/constants/search'
import {
  LocationIconContainer,
  QuickFilterButtonStyles,
  QuickFilterButtonText,
} from '@/features/filters/components/QuickFilters/styled'
import { getQuickFilterLabel } from '@/features/filters/utils/getQuickFilterLabel'
import { isFilterApplied } from '@/features/filters/utils/isFilterApplied'
import { Flex } from '@/ui/atoms/flex'

type QuickFilterButtonProps = {
  filter: Filter
  onClick: () => void
  area?: LocationArea | null
  /**
   * Describes if the quick filter should be hidden on desktop view
   */
  hideOnDesktop?: boolean
  buttonTextCustomStyle?: string
}

const LocationSolidIcon = dynamic(() => import('@kijiji/icons/src/icons/LocationSolid'), {
  ssr: false,
})

export const QuickFilterButton = ({
  filter,
  onClick,
  area,
  hideOnDesktop,
  buttonTextCustomStyle,
}: QuickFilterButtonProps) => {
  const isSelected = isFilterApplied(filter)
  const { t } = useTranslation([TRANSLATION_KEYS.SRP])
  const label = getQuickFilterLabel(filter, t, area) ?? filter.label

  // Location is a special case since the button has an icon
  const isLocationFilter = filter.name === FILTER_CANONICAL.LOCATION

  return (
    <QuickFilterButtonStyles
      shape="pill"
      size="compact"
      variant="subtle"
      onClick={onClick}
      isSelected={isSelected}
      $hideOnDesktop={hideOnDesktop}
    >
      <Flex
        justifyContent="space-around"
        alignItems="center"
        gap={theme.spacing.mini}
        flexGrow={1}
        style={{ maxWidth: '100%' }}
      >
        {isLocationFilter && (
          <LocationIconContainer>
            <LocationSolidIcon aria-hidden="true" />
          </LocationIconContainer>
        )}
        <QuickFilterButtonText
          isLocationFilter={isLocationFilter}
          isRadialLocation={!!area}
          buttonTextCustomStyle={buttonTextCustomStyle}
        >
          {label}
        </QuickFilterButtonText>
        <ChevronLeftSmallIcon size="xsm" style={{ flexShrink: 0 }} />
      </Flex>
    </QuickFilterButtonStyles>
  )
}
